import queryString from 'query-string'

/**
 * Returns primaryLocation if it is set in location state.
 *
 * This is useful within modal context to access the root location instead of modal location.
 * In modal context useLocation hook and withRouter HoC will return modal location.
 *
 * Example modal path where
 * - primary location pathname is after the domain (site.com)
 * - modal location pathname is after the hash (#)
 *
 * "site.com/primary/location/here#/modal/location/here"
 */
export const getPrimaryLocation = (location) => {
    return location.state && location.state.primaryLocation ? location.state.primaryLocation : location
}

export const writeToPrimaryLocationState = (history, state, location = null) => {
    const primaryLocation = getPrimaryLocation(location || history.location)
    history.replace({
        ...primaryLocation,
        state: {
            ...primaryLocation.state,
            ...state,
        }
    })
}

export const getFullPath = (location, options = {}) => {
    const primaryLocation = getPrimaryLocation(location)
    return locationObjectToString(primaryLocation, options)
}

export const deleteLocationSearchParam = (location, paramName) => {
    const query = queryString.parse(location.search)
    if (Array.isArray(paramName)) {
        paramName.forEach(param => {
            delete query[param]
        })
    } else {
        delete query[paramName]
    }
    return writeLocationSearch(location, query)
}

export const addLocationSearchParams = (location, params) => {
    const query = queryString.parse(location.search)
    return writeLocationSearch(location, { ...query, ...params })
}

export const writeLocationSearch = (location, query) => {
    const queryStr = queryString.stringify(query)
    const newSearch = queryStr && queryStr.length ? `?${queryStr}` : ''

    const isPrimaryLocation = !location.state || !location.state.primaryLocation

    if (isPrimaryLocation) {
        return {
            ...location,
            search: newSearch,
        }
    } else {
        const primaryLocation = location.state.primaryLocation

        const hashParts = primaryLocation && primaryLocation.hash ? primaryLocation.hash.split('?') : location.hash.split('?')
        const newHash = hashParts ? `${hashParts[0]}${newSearch}` : ''

        return {
            ...location,
            hash: newHash,
            state: {
                ...(location.state || {}),
                primaryLocation: {
                    ...primaryLocation,
                    hash: newHash,
                }
            }
        }
    }
}

export const locationObjectToString = (location, options = {}) => {
    options = {
        path: true,
        search: true,
        hash: true,
        ...options,
    }

    return location ? [
        `${options.path ? location.pathname || '' : ''}`,
        `${options.search ? location.search || '' : ''}`,
        `${options.hash ? location.hash || '' : ''}`,
    ].join('') : ''
}

export const stringToLocationObject = (url) => {
    const [beforeHash, ...afterHash] = url.split('#')
    const searchSplit = beforeHash.split('?')

    const pathname = searchSplit[0]
    const search = searchSplit[1]

    // Use join as there can be more than one hash in path and now they are split.
    // Example could be where modal has target path (as query pram) that points to another modal:
    // http://localhost:8080/#/modal/login?loginSuccessTarget=/#/modal/ajoneuvon-valinta/goto?target=/ajoneuvo/:vehicleId/tapahtumat#/modal/ajoneuvo/:vehicleId/hinta-arvio
    const hash = afterHash.join('#')

    return {
        pathname: pathname,
        search: search,
        hash: hash,
    }
}
